import dayjs from "@/lib/dayjs"
import type { BaseGeojson, GeojsonPosition, PerimeterGeojson } from "@/models/lib/maps"
import { AxiosResponse } from "axios"
import { Editor } from "tinymce"

export const isEmailValid = (email: any): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z][a-zA-Z]+/
    return email.match(emailRegex) !== null
}

export const downloadExcelFile = (response: AxiosResponse): void => {
    const blob = new Blob([response.data], {
        type: (response.headers["content-type"] as string) || undefined
    })
    const link = document.createElement("a")
    link.href = globalThis.URL.createObjectURL(blob)
    link.download = getFileNameFromResponse(response)
    document.body.appendChild(link)
    link.click()
}

const getFileNameFromResponse = (response: AxiosResponse): string => {
    const defaultFileName = "unknown_filename.xlsx"
    const contentDisposition: string | undefined = response.headers["content-disposition"]

    if (!contentDisposition) return defaultFileName

    const regExpFilename = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
    const matches = regExpFilename.exec(contentDisposition)
    return matches?.[1] ? matches[1].replace(/^["']|["']$/g, "") : defaultFileName
}

export const computeDistance = (lat1: any, lon1: any, lat2: any, lon2: any, unit = "K") => {
    const radlat1 = (Math.PI * lat1) / 180
    const radlat2 = (Math.PI * lat2) / 180
    const theta = lon1 - lon2
    const radtheta = (Math.PI * theta) / 180
    let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)
    if (dist > 1) {
        dist = 1
    }
    dist = Math.acos(dist)
    dist = (dist * 180) / Math.PI
    dist = dist * 60 * 1.1515
    if (unit == "K") {
        dist = dist * 1.609344 * 1000
    }
    if (unit == "N") {
        dist = dist * 0.8684
    }
    return dist / 2
}

export const getLastWeek = () => {
    const now = new Date()
    return now.setDate(now.getDate() - 7)
}

export const computeSvg = () => {
    return "M21.8509 3.59605C16.8522 -1.19868 8.74777 -1.19868 3.7491 3.59605C-0.853195 8.01057 -1.26898 15.0351 2.78282 19.9209L12.8 32L22.8172 19.9209C26.869 15.0351 26.4532 8.01058 21.8509 3.59605Z"
}

export const getImageDimensions = (imageBase64: any) => {
    const img = new Image()
    img.src = imageBase64
    return new Promise(resolve => {
        img.onload = (img: any) => {
            resolve({ width: img.target.width, height: img.target.height })
        }
    })
}

export const normalizeString = (str?: string | null): string => {
    if (!str) return ""
    return str
        .toString()
        .toLowerCase()
        .normalize("NFD")
        .replaceAll(/[\u0300-\u036f]/g, "") // ignore accents
}

export const normalizedFilter = (query: string, array: any[], key: string) => {
    if (!array) return []
    return array.filter(el => {
        return normalizeString(el[key]).includes(normalizeString(query))
    })
}

export const formatDate = (date: any, hours: boolean = false) => {
    const format = hours ? "DD-MM-YYYY HH:mm" : "DD-MM-YYYY"
    return dayjs(date).format(format)
}

export const formatTime = (date: string | number) => {
    return dayjs(date).format("HH:mm")
}

export const isNumber = (value: any) => typeof value === "number"

export const copyObjectToReactive = (source: any, vueReactiveTarget: any) => {
    cleanExistingProperties(vueReactiveTarget)
    for (const [key, value] of Object.entries(source)) {
        vueReactiveTarget[key] = value
    }
}

const cleanExistingProperties = (vueReactiveObject: any) => {
    Object.keys(vueReactiveObject).forEach(key => delete vueReactiveObject[key])
}

export const replacePTagsByBr = (htmlString?: string) => {
    if (!htmlString) return ""
    const brString = "<br/>"
    let message = htmlString.replaceAll("<p>", "")
    message = message.replaceAll("</p>", brString)
    return htmlString.includes("<p>") ? message.slice(0, -brString.length) : message
}

export const replacePTagsByLineBreak = (htmlString?: string) => {
    if (!htmlString) return ""
    const lineBreakString = "\n"
    let message = htmlString.replaceAll("<p>", "")
    message = message.replaceAll(lineBreakString, "")
    message = message.replaceAll("</p>", lineBreakString)
    return htmlString.includes("<p>") ? message.slice(0, -lineBreakString.length) : message
}

export const getTextFormattedForTinyMceEditor = (str?: string) => {
    if (!str) return ""
    let messageLines = str.split("\n")
    messageLines = messageLines.map((messageLine: any) => `<p>${messageLine}</p>`)
    return messageLines.join("")
}

export const getTextFormattedForDatabase = (str?: string): string => {
    if (str) {
        str = replacePTagsByBr(str)
        str = str.replaceAll("\n", "")
        return str
    }
    return ""
}

export const getPerimeterMapFormattedGeojson = (geojson: BaseGeojson): PerimeterGeojson => {
    return {
        type: "FeatureCollection",
        features: [
            {
                geometry: geojson,
                type: "Feature"
            }
        ]
    }
}

export const getPerimeterMapFormattedGeojsonArray = (geojsonArray: BaseGeojson[]): PerimeterGeojson => {
    return {
        type: "FeatureCollection",
        features: geojsonArray.flatMap(geojson => getPerimeterMapFormattedGeojson(geojson).features)
    }
}

// Structural GeoJSON validation (not geometric: does not detect self-intersecting polygons).
// A linear ring must be closed ([lng, lat] of first === last) and have at least 4 positions.
const isValidPosition = (position: unknown): position is GeojsonPosition => {
    return (
        Array.isArray(position) &&
        position.length >= 2 &&
        position.every(coordinate => typeof coordinate === "number" && Number.isFinite(coordinate))
    )
}

const isValidLinearRing = (ring: unknown): boolean => {
    if (!Array.isArray(ring) || ring.length < 4 || !ring.every(isValidPosition)) {
        return false
    }
    const first = ring[0] as GeojsonPosition
    const last = ring[ring.length - 1] as GeojsonPosition
    return first[0] === last[0] && first[1] === last[1]
}

const isValidPolygonRings = (rings: unknown): boolean => {
    return Array.isArray(rings) && rings.length > 0 && rings.every(isValidLinearRing)
}

export const isValidGeoJsonGeometry = (value: unknown): value is BaseGeojson => {
    if (typeof value !== "object" || value === null) {
        return false
    }
    const geometry = value as { type?: unknown; coordinates?: unknown }
    if (geometry.type === "Polygon") {
        return isValidPolygonRings(geometry.coordinates)
    }
    if (geometry.type === "MultiPolygon") {
        return (
            Array.isArray(geometry.coordinates) &&
            geometry.coordinates.length > 0 &&
            geometry.coordinates.every(isValidPolygonRings)
        )
    }
    return false
}

export interface GeojsonArrayParseResult {
    valid: boolean
    geojsons: BaseGeojson[]
    error?: string
}

export const parseGeojsonArray = (text: string): GeojsonArrayParseResult => {
    if (text.trim() === "") {
        return { valid: false, geojsons: [], error: "At least one GeoJSON is required" }
    }
    let parsed: unknown
    try {
        parsed = JSON.parse(text)
    } catch {
        return { valid: false, geojsons: [], error: "Invalid JSON" }
    }
    if (!Array.isArray(parsed)) {
        return { valid: false, geojsons: [], error: "Expected a list of GeoJSON" }
    }
    if (parsed.length === 0) {
        return { valid: false, geojsons: [], error: "At least one GeoJSON is required" }
    }
    const invalidIndex = parsed.findIndex(item => !isValidGeoJsonGeometry(item))
    if (invalidIndex !== -1) {
        return { valid: false, geojsons: [], error: `Item ${invalidIndex + 1} must be a valid GeoJSON geometry` }
    }
    return { valid: true, geojsons: parsed as BaseGeojson[] }
}

export const numberToStringWithSpaces = (num: number) => {
    num = Number(num.toFixed(2))
    return num.toLocaleString("en-US").replaceAll(",", " ")
}

export const convertKmToMeters = (kmValue: number): number => {
    return Math.trunc(kmValue * 1000)
}

export const convertKmToMetersWithDefaultReturn = <T>(
    kmValue: number | undefined | null,
    defaultReturnValue: T
): number | T => {
    return typeof kmValue === "number" ? convertKmToMeters(kmValue) : defaultReturnValue
}

export const convertMetersToKm = (meterValue: number): number => {
    return meterValue / 1000
}

export const convertMetersToKmWithDefaultReturn = <T>(
    meterValue: number | undefined | null,
    defaultReturnValue: T
): number | T => {
    return typeof meterValue === "number" ? convertMetersToKm(meterValue) : defaultReturnValue
}

export const convertEurosToCentDisplay = (amount: number): string => {
    return (amount / 100).toFixed(2)
}

export const convertDisplayCurrencyToCents = (euroValue: number): number => {
    return Math.trunc(euroValue * 100)
}

export const convertDisplayCurrencyToCentsWithDefaultReturn = <T>(
    value: number | undefined | null,
    defaultReturnValue: T
): number | T => {
    return typeof value === "number" ? convertDisplayCurrencyToCents(value) : defaultReturnValue
}

export const convertCentsToDisplayValue = (centValue: number): number => {
    return centValue / 100
}

export const convertCentsToDisplayValueWithDefaultReturn = <T>(
    centValue: number | undefined | null,
    defaultReturnValue: T
): number | T => {
    return typeof centValue === "number" ? convertCentsToDisplayValue(centValue) : defaultReturnValue
}

export const convertMinutesToSeconds = (timeValue: number): number => {
    return Math.trunc(timeValue * 60)
}

export interface InitializationOptions {
    firstNameToReplace: string | null
    defaultText?: string
}

export const setEditorContent = (editor: Editor | null, text: string): string => {
    const formattedText = getTextFormattedForTinyMceEditor(text)
    editor?.setContent(formattedText)
    return formattedText
}

export const computeExternalLink = (url: string) => {
    return url.includes("http") ? url : `${import.meta.env.VITE_API_BACKEND_URL}${url}`
}

export const sortByName = <T extends { name: string }>(array: T[]): T[] => {
    return [...array].sort((a, b) => a.name.localeCompare(b.name))
}

export const formatSnakeCaseToTitleCase = (key: string): string => {
    return key.replaceAll("_", " ").replaceAll(/\b\w/g, letter => letter.toUpperCase())
}

export const TOO_MANY_REQUESTS_MESSAGE =
    "You have reached the maximum number of requests allowed. Please try again later."

const extractResponseDataMessage = (data: unknown): string | undefined => {
    // Ignore HTML bodies (e.g. error pages served by a proxy or load balancer)
    if (typeof data === "string") return data.length > 0 && !data.trimStart().startsWith("<") ? data : undefined
    if (typeof data !== "object" || data === null) return undefined
    if ("detail" in data && typeof (data as Record<string, unknown>).detail === "string") {
        return (data as Record<string, unknown>).detail as string
    }
    const parts: string[] = (Object.values(data) as unknown[]).flat().filter((v): v is string => typeof v === "string")
    return parts.length > 0 ? parts.join(". ") : undefined
}

export const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === "object" && "response" in error) {
        const { response } = error as { response?: { status?: number; data?: unknown } }
        const status = response?.status
        const message = status === 500 ? undefined : extractResponseDataMessage(response?.data)
        if (status === 429) return message ?? TOO_MANY_REQUESTS_MESSAGE
        return message ? `${message} (${status})` : `An unexpected error occurred (${status ?? "unknown"})`
    }
    if (typeof error === "string" && error.length > 0) return error
    if (error instanceof Error && error.message) return error.message
    return "An unexpected error occurred"
}

export const formatDisplayValue = (value: unknown, truncateObjects: boolean = true): string => {
    if (typeof value === "boolean") return value ? "Yes" : "No"
    if (value === null || value === undefined) return "-"
    if (typeof value === "object") {
        const json = JSON.stringify(value)
        return truncateObjects && json.length > 50 ? json.slice(0, 50) + "..." : json
    }
    return String(value)
}

export const trimSemicolonList = (list?: string | null): string => {
    if (!list) return ""
    const trimmed = list.split(";").map(entry => entry.trim())
    const nonEmpty = trimmed.filter(entry => entry !== "")
    return nonEmpty.join(";")
}
